'use client';
import { useEffect } from 'react';
import ProjectCard from '@/app/homepage/components/ProjectCard';
import Footer from '@/components/common/Footer';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import Navigation from '@/components/common/Navigation';

export default function ProjectsPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach(el => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <main className="min-h-screen bg-background">
      <Navigation />
      {/* Page Header */}
      <section className="py-20 md:py-32 border-b border-border pt-32 md:pt-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-primary mb-4 block">
              Case Studies
            </span>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground mb-6">
              Proven Project Excellence
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Real projects. Real results. See how we&apos;ve helped energy companies, telecom providers,
              and municipalities deliver critical infrastructure on time and under budget.
            </p>
          </div>
        </div>
      </section>

      {/* Project Cards */}
      <section className="border-b border-border">
        <div className="border-t border-border">
          <ProjectCard
            title="Park Crossing Subdivision TUG Reconduct"
            location="Pineville, NC"
            challenge="Install approximately 147,000 linear feet of targeted overhead to underground power lines. This project consisted of three-phase 750MCM duct banks, multiple loops of 25KVA primary in 2&quot; conduit and 6/3-350/3 wire services to homes."
            solution="Deploy 6-8 drill crews at any given moment from Vermeer D10x15 and Ditch Witch JT10 up to Vermeer D23x30 and JT24 due to space restrictions and ground conditions."
            image="/assets/images/IMG_0455_Medium-1775172181226.jpeg"
            imageAlt="Vermeer D20x22 directional drilling equipment on site at Park Crossing Subdivision TUG Reconduct project"
            delay={100}
          />

          <ProjectCard
            title="Hwy 548 6&quot; Feeder Main"
            location="Conway, SC"
            challenge="Install approximately 5,300 linear feet of 6&quot; HDPE Gas pipeline for Dominion Energy. This project included two separate 900' long Tier 3 bores under protected bodies of water at a maximum 65 foot depth while maintained a bore profile within 1 foot of the engineered drawings while mitigating inadvertent returns."
            solution="Utilize a Vermeer D40x55 and hydro excavation truck to perform the long tier 3 bores and mobilize two additional drill crews with Vermeer D23x30 and Ditch Witch JT24 to install the remaining 3,500 feet of 6&quot; HDPE simultaneously."
            image="/assets/images/IMG_1219-1786126182611.jpeg"
            imageAlt="East Coast Underground truck and Vermeer equipment on a flatbed trailer for Hwy 548 6 inch Feeder Main project"
            delay={200}
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-24 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-6 text-center reveal">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            Get a detailed quote within 72 hours. Our team is ready to deliver.
          </p>
          <Link
            href="/homepage#rfp-inquiry"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-lg font-bold text-lg hover:bg-primary/90 transition-all group"
          >
            RFP Inquiry
            <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
